import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ExpandablePanel, EventsItemFooter, EventsItemHeader, EventsItemContent } from 'components/widgets';
import classNames from 'classnames';
import { getEventSlotGroupType } from '../utils';
import css from '../page.css';
import { EVENT_TYPES } from '../../utils/consts';
import { EVENT_TYPE_BORDER_CLASSES } from '../consts';

function CustomEventEditor({
    onEventExpand = () => {},
    customEvent = {},
    uans = [],
    calculatedMetricsOptions = [],
    onUANEventRemoved = () => {},
    onUANEventAdded = () => {},
    onToggleCalculatedMetric = () => {},
    onCancelClicked = () => {},
    onUniqueToggle = () => {},
    warnings = [],
    saving = false,
    saveError = '',
    uniqueToggleDisabled = false,
    showSlotNumber = false,
    saveDisabled,
    eventType,
    disabled,
    activeGroupType,
    readOnly = false,
    dataTestId,
    onSaveClicked = () => {},
    onEventNameChanged = () => {},
    onDeleteClicked = () => {},
}) {
    const handleSaveClicked = useCallback(() => {
        onSaveClicked(customEvent);
    }, [onSaveClicked, customEvent]);

    const handleEventNameChanged = useCallback(
        value => {
            onEventNameChanged(customEvent, value);
        },
        [onEventNameChanged, customEvent]
    );

    const handleDeleteClicked = useCallback(() => {
        onDeleteClicked(customEvent);
    }, [onDeleteClicked, customEvent]);

    const { name, create_ts, creator, id, last_update_ts, expanded, is_locked, slot, is_unique } = customEvent;

    return (
        <ExpandablePanel
            id={id}
            disabled={is_locked || disabled}
            onExpandClick={onEventExpand}
            expanded={expanded}
            isSticky
            className={classNames({
                [EVENT_TYPE_BORDER_CLASSES[eventType]]: eventType,
                [css.eventItemBorder]: eventType,
            })}
            headerContainerClassName={css.eventHeaderContainer}
            isExpandable={eventType !== EVENT_TYPES.STANDARD}
            dataTestId={dataTestId}
            footer={
                <EventsItemFooter
                    modifiedBy=""
                    createdBy={creator}
                    lastModified={last_update_ts}
                    created={create_ts}
                    onCancelClicked={onCancelClicked}
                    onSaveClicked={handleSaveClicked}
                    saving={saving}
                    slot={slot}
                    saveDisabled={saveDisabled}
                    readOnly={readOnly}
                />
            }
            header={
                <EventsItemHeader
                    disabled={disabled || saving}
                    eventName={name}
                    lastModified={last_update_ts}
                    expanded={expanded}
                    locked={is_locked}
                    calculatedMetricsOptions={calculatedMetricsOptions}
                    onEventNameChanged={handleEventNameChanged}
                    onDeleteClicked={handleDeleteClicked}
                    slot={slot}
                    showSlotNumber={showSlotNumber}
                    unique={is_unique}
                    eventSlotGroupType={getEventSlotGroupType(customEvent)}
                    readOnly={readOnly}
                    standard={eventType === EVENT_TYPES.STANDARD}
                />
            }
            content={
                <EventsItemContent
                    uans={uans}
                    calculatedMetricsOptions={calculatedMetricsOptions}
                    customEvent={customEvent}
                    onUANEventAdded={onUANEventAdded}
                    onUANEventRemoved={onUANEventRemoved}
                    onToggleCalculatedMetric={onToggleCalculatedMetric}
                    onUniqueToggle={onUniqueToggle}
                    warnings={warnings}
                    saving={saving}
                    saveError={saveError}
                    uniqueToggleDisabled={uniqueToggleDisabled}
                    activeGroupType={activeGroupType}
                    readOnly={readOnly}
                    standard={eventType === EVENT_TYPES.STANDARD}
                />
            }
        />
    );
}

CustomEventEditor.propTypes = {
    onEventExpand: PropTypes.func,
    customEvent: PropTypes.objectOf(PropTypes.any),
    uans: PropTypes.arrayOf(PropTypes.any),
    calculatedMetricsOptions: PropTypes.arrayOf(PropTypes.any),
    warnings: PropTypes.arrayOf(PropTypes.any),
    onUANEventAdded: PropTypes.func,
    onUANEventRemoved: PropTypes.func,
    onToggleCalculatedMetric: PropTypes.func,
    onCancelClicked: PropTypes.func,
    onSaveClicked: PropTypes.func,
    saving: PropTypes.bool,
    saveError: PropTypes.string,
    onEventNameChanged: PropTypes.func,
    onDeleteClicked: PropTypes.func,
    onUniqueToggle: PropTypes.func,
    uniqueToggleDisabled: PropTypes.bool,
    showSlotNumber: PropTypes.bool,
    eventType: PropTypes.string,
    readOnly: PropTypes.bool,
    dataTestId: PropTypes.string,
};

export default CustomEventEditor;
